/**
 * The phone planner sheet's geometry, kept pure so it can be tested.
 *
 *   full ── the sheet fills the space under the top bar
 *   half ── the sheet's top sits near the middle of the screen
 *   peek ── summary + action bar; the map gets the rest
 *
 * Heights are measured from the bottom of the map area (the sheet's parent).
 */
export type SheetSnap = 'peek' | 'half' | 'full';

export const SHEET_ORDER: SheetSnap[] = ['peek', 'half', 'full'];

/**
 * Grab handle (24) + the trip header (62) + the 56px action bar (57 with its
 * hairline). The home-bar inset is added on top at runtime.
 */
export const PEEK_HEIGHT = 144;
/** The empty planner's peek: heading, start field and a row of real trips. */
export const EMPTY_PEEK_HEIGHT = 176;
/** Below this the map is too small to bother with its controls. */
export const MIN_MAP_FOR_CONTROLS = 220;

export interface SnapHeights {
  peek: number;
  half: number;
  full: number;
}

/**
 * Snap heights for a map area `containerH` tall. `viewportH` places half so
 * its top sits just above the middle of the screen, whatever the top bar.
 */
export function snapHeights(containerH: number, viewportH: number = containerH, peek: number = PEEK_HEIGHT): SnapHeights {
  const full = Math.max(0, Math.round(containerH));
  const p = Math.min(peek, full);
  // Half: the sheet's top at 50% of the screen (never below peek, never
  // above full).
  const half = Math.min(full, Math.max(p + 80, Math.round(viewportH * 0.52)));
  return { peek: p, half: Math.max(p, half), full };
}

export function snapHeight(snap: SheetSnap, h: SnapHeights): number {
  return h[snap];
}

/** One step up or down the ladder, clamped. */
export function stepSnap(snap: SheetSnap, dir: 1 | -1): SheetSnap {
  const i = SHEET_ORDER.indexOf(snap);
  return SHEET_ORDER[Math.max(0, Math.min(SHEET_ORDER.length - 1, i + dir))];
}

/** A tap on the handle: peek → half → full → half. */
export function tapSnap(snap: SheetSnap): SheetSnap {
  return snap === 'peek' ? 'half' : snap === 'half' ? 'full' : 'half';
}

/**
 * Where a released drag lands. `height` is the sheet's height at release,
 * `velocity` is px/ms with positive = finger moving DOWN (the sheet
 * shrinking). A flick goes one step in its direction from the snap it
 * started at; a slow drag lands on the nearest snap to where it was let go,
 * projected a little along its motion.
 */
export function resolveSnap(height: number, velocity: number, from: SheetSnap, h: SnapHeights): SheetSnap {
  const FLICK = 0.45; // px/ms
  if (velocity <= -FLICK) {
    // Flick up: the next snap above where the sheet is now.
    const above = SHEET_ORDER.find((s) => h[s] > height + 4);
    return above ?? 'full';
  }
  if (velocity >= FLICK) {
    const below = [...SHEET_ORDER].reverse().find((s) => h[s] < height - 4);
    return below ?? 'peek';
  }
  const projected = height - velocity * 160;
  let best: SheetSnap = from;
  let bestD = Infinity;
  for (const s of SHEET_ORDER) {
    const d = Math.abs(h[s] - projected);
    if (d < bestD) {
      best = s;
      bestD = d;
    }
  }
  return best;
}

/**
 * Rubber-banding past the ends: free inside [min, max], then a third of
 * the overshoot, so the sheet resists instead of stopping dead.
 */
export function clampWithResistance(height: number, min: number, max: number): number {
  if (height > max) return max + (height - max) / 3;
  if (height < min) return min - (min - height) / 3;
  return height;
}

/**
 * Velocity from the last few samples (px/ms, + = down). Ignores samples
 * older than 100ms so a pause before letting go reads as no flick.
 */
export function releaseVelocity(samples: { t: number; y: number }[], now: number): number {
  const recent = samples.filter((s) => now - s.t <= 100);
  if (recent.length < 2) return 0;
  const a = recent[0];
  const b = recent[recent.length - 1];
  const dt = b.t - a.t;
  return dt > 0 ? (b.y - a.y) / dt : 0;
}

export interface RowBox {
  /** px from the sheet's top edge */
  top: number;
  bottom: number;
}

/**
 * The half detent, nudged so the list's visible bottom edge never slices a
 * row (a stop, a stepper, a day heading). Row positions are measured from
 * the sheet's top, so they hold at any sheet height; the list's visible
 * edge sits `footer` px above the sheet's bottom (the action bar).
 *
 * A row cut by the edge is either pushed fully out of view (the sheet
 * shrinks to the row's top) or brought fully into view (it grows to the
 * row's bottom), whichever moves less, within `maxShrink` / `maxGrow`.
 * Nested rows (a stepper inside a stop) count as one: the outermost extent.
 */
export function alignHeightToRows(
  height: number,
  rows: RowBox[],
  footer: number,
  opts: { maxShrink: number; maxGrow: number; min?: number; max?: number }
): number {
  const lo = Math.max(opts.min ?? -Infinity, height - opts.maxShrink);
  const hi = Math.min(opts.max ?? Infinity, height + opts.maxGrow);
  let h = height;
  for (let pass = 0; pass < 3; pass++) {
    const edge = h - footer;
    const cut = rows.filter((r) => r.top < edge - 1 && r.bottom > edge + 1);
    if (!cut.length) return Math.round(h);
    const top = Math.min(...cut.map((r) => r.top));
    const bottom = Math.max(...cut.map((r) => r.bottom));
    const shrinkTo = top + footer;
    const growTo = bottom + footer;
    const options = [shrinkTo, growTo].filter((x) => x >= lo && x <= hi);
    if (!options.length) return Math.round(height);
    h = options.reduce((best, x) => (Math.abs(x - height) < Math.abs(best - height) ? x : best));
  }
  return Math.round(h);
}
